/**
 * Partner theme management for Questions Bar Widget
 *
 * Automatically applies partner-specific CSS themes based on the current domain.
 * Themes are embedded in the bundle and injected as <style> tags - no external files needed.
 */

import { getThemeCss } from './themes/themeData.js';

// Map of domain patterns to theme names
// The widget checks if the current hostname ends with these patterns
export const PARTNER_THEMES = {
  'autoblog.com': 'autoblog',
  'localhost': 'localhost',
  '127.0.0.1': 'localhost',
  'boingboing.net': 'boingboing',
  'biztoc.com': 'biztoc',
};

/**
 * Detects the current domain and returns the matching theme name if any
 * @returns {string|null} Theme name or null if no match
 */
export function detectPartnerTheme() {
  const hostname = window.location?.hostname?.toLowerCase() || '';
  for (const [domain, themeName] of Object.entries(PARTNER_THEMES)) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return themeName;
    }
  }

  return null;
}

/**
 * Injects a partner theme as an inline <style> tag
 * @param {string} themeName - The theme name (e.g., 'autoblog')
 * @returns {HTMLStyleElement|null} The created style element or null
 */
export function injectPartnerTheme(themeName) {
  if (!themeName) return null;

  // Check if theme is already loaded
  const existingStyle = document.querySelector(`style[data-gist-theme="${themeName}"]`);
  if (existingStyle) {
    return existingStyle;
  }

  // Get the CSS content from embedded themes
  const cssContent = getThemeCss(themeName);
  if (!cssContent) {
    console.warn(`[GistWidget] Theme "${themeName}" not found in embedded themes`);
    return null;
  }

  // Create and inject the inline style
  const style = document.createElement('style');
  style.textContent = cssContent;
  style.setAttribute('data-gist-theme', themeName);

  // Insert into head
  if (document.head) {
    document.head.appendChild(style);
  }

  return style;
}

/**
 * Automatically detects and applies the appropriate partner theme
 * @returns {HTMLStyleElement|null} The created style element or null if no theme matched
 */
export function applyPartnerTheme() {
  const themeName = detectPartnerTheme();
  if (themeName) {
    return injectPartnerTheme(themeName);
  }
  return null;
}

/**
 * Check if a theme is currently loaded
 * @param {string} themeName - The theme name to check
 * @returns {boolean}
 */
export function isThemeLoaded(themeName) {
  return !!document.querySelector(`style[data-gist-theme="${themeName}"]`);
}

/**
 * Remove a loaded theme
 * @param {string} themeName - The theme name to remove
 * @returns {boolean} True if theme was found and removed
 */
export function removePartnerTheme(themeName) {
  const style = document.querySelector(`style[data-gist-theme="${themeName}"]`);
  if (style && style.parentNode) {
    style.parentNode.removeChild(style);
    return true;
  }
  return false;
}
